<template>
  <div class="p-2 flex items-center gap-2">
    <div
      v-if="authStore.isLoggedIn"
      class="flex items-center gap-2"
    >
      <QBtn
        v-if="$q.screen.gt.sm"
        :to="{ name: 'deposits' }"
        dense
        unelevated
        class="bg-gradient-secondary md:(py-2 px-4)"
      >
        <div class="w-full text-light-1 flex items-center justify-center gap-2">
          <SvgIcon
            name="deposits"
            size="xs"
          />
          입금
        </div>
      </QBtn>
      <QBtn
        v-if="$q.screen.gt.sm"
        :to="{ name: 'withdrawals' }"
        dense
        unelevated
        class="bg-gradient-primary md:(py-2 px-4)"
      >
        <div class="w-full text-light-1 flex items-center justify-center gap-2">
          <SvgIcon
            name="withdrawals"
            size="xs"
          />
          출금
        </div>
      </QBtn>
      <div
        class="p-1 rounded hover:(dark:bg-dark-3 light:bg-light-3 cursor-pointer)"
        @mouseover="show = true"
        @click="show = true"
      >
        <div class="rounded-full bg-[#814BF6] w-8 h-8 flex items-center justify-center overflow-hidden">
          <QIcon
            name="person"
            size="lg"
          />
        </div>
      </div>
      <div class="h-full">
        <QTooltip
          ref="tooltip"
          v-model="show"
          :offset="[0, 40]"
          class="w-66 h-142 p-0 dark:(bg-dark-2 text-dark-text) light:(bg-light-2 text-light-text) "
          style="pointer-events: auto !important;"
        >
          <QScrollArea class="flex-grow h-full flex flex-col gap-1 p-2 text-sm">
            <div class="h-21 grid grid-cols-2 gap-1">
              <div
                class="item"
              >
                <span class="bg-primary px-1 rounded text-light-1">{{ `Lv.${authStore.user.level_info.level}` }}</span>
                <span class="truncate">{{ authStore.user.nickname }}</span>
              </div>
              <div
                class="item"
              >
                <SvgIcon
                  name="wallet"
                  size="18px"
                />
                <span class="truncate">{{ authStore.walletBalance }}</span>
              </div>
              <div
                class="item"
              >
                <SvgIcon
                  name="point"
                  size="18px"
                />
                <span class="truncate">{{ authStore.pointBalance }}</span>
              </div>
              <div
                class="item"
              >
                <SvgIcon
                  name="gold"
                  size="18px"
                />
                <span class="truncate">{{ authStore.coinBalance }}</span>
              </div>
            </div>
            <div class="flex h-full flex-col gap-2">
              <template v-for="nav in userNavs">
                <QBtn
                  :to="nav.route"
                  dense
                  unelevated
                >
                  <div class="w-full px-1 text-base dark:text-dark-text light:text-light-text flex items-center justify-start gap-5">
                    <SvgIcon
                      :name="nav.icon"
                      size="xs"
                    />
                    {{ nav.label }}
                    <div v-if="nav.count && nav.count.value > 0">
                      ({{ nav.count }})
                    </div>
                  </div>
                </QBtn>
              </template>
              <div class="w-full border-t dark:border-dark-icon light:border-light-icon pt-2">
                <QBtn
                  dense
                  unelevated
                  class="w-full"
                  @click.capture="authStore.logout()"
                >
                  <div class="w-full px-1 text-base dark:text-dark-text light:text-light-text flex items-center justify-start gap-5">
                    <ImgIcon
                      :src="Logout"
                      size="xs"
                    />
                    로그아웃
                  </div>
                </QBtn>
              </div>
            </div>
          </QScrollArea>
        </QTooltip>
      </div>
    </div>
    <div
      v-else
      class="flex items-center gap-2"
    >
      <QBtn
        dense
        unelevated
        class="text-dark-text text-sm p-2"
        label="로그인"
        @click="authStore.showLoginModal = true"
      />
      <QBtn
        :to="{name:'registration'}"
        dense
        unelevated
        label="가입하기"
        class="bg-gradient-secondary p-2"
      />
    </div>
    <DarkModeToggle />
    <QBtn
      v-if="authStore.isLoggedIn && $q.screen.lt.md"
      dense
      unelevated
      icon="shopping_cart"
      class="text-light-icon"
      @click="configStore.toggleRightDrawer()"
    >
      <QBadge
        v-if="cartStore.items.length > 0"
        floating
        color="red"
        rounded
      >
        {{ cartStore.items.length }}
      </QBadge>
    </QBtn>
  </div>
</template>

<script setup>
import Logout from '@/icons/profile/logout.svg'
import SvgIcon from '@/components/Icons/SvgIcon.vue'
import ImgIcon from '@/components/Icons/ImgIcon.vue'
import DarkModeToggle from './DarkModeToggle.vue'
import {useAuthStore} from '@/stores/auth'
import {useConfigStore} from '@/stores/config'
import {onClickOutside} from '@vueuse/core'
import {computed, ref} from 'vue'
import {map, sum} from 'lodash-es'
import {useCartStore} from '@/stores/cart'

const authStore = useAuthStore()
const configStore = useConfigStore()
const cartStore = useCartStore()

const show = ref(false)
const tooltip = ref(null)

const userNavs = [
  {
    icon: 'deposits',
    label: '입금',
    route: { name: 'deposits' }
  },
  {
    icon: 'withdrawals',
    label: '출금',
    route: { name: 'withdrawals' }
  },
  {
    icon: 'questions',
    label: '고객센터',
    route: { name: 'questions' }
  },
  {
    icon: 'bets',
    label: '배팅내역',
    route: { name: 'bets', params: { category: 'pregame' } }
  },
  {
    icon: 'announcements',
    label: '공지사항',
    route: { name: 'announcements' }
  },
  {
    icon: 'events',
    label: '이벤트',
    route: { name: 'events' }
  },
  {
    icon: 'consumables',
    label: '아이템',
    route: { name: 'consumables' },
    count: computed(() => sum(map(authStore.user.consumables, v => v.count)))
  },
  {
    icon: 'coupons',
    label: '쿠폰',
    route: { name: 'coupons' },
    count: computed(() => authStore.user.coupons.length),
  },
  {
    icon: 'messages',
    label: '쪽지함',
    route: { name: 'messages' },
    count: computed(() => authStore.user.new_messages_count),
  },
  {
    icon: 'attendances',
    label: '출석체크',
    route: { name: 'attendances' }
  },
  {
    icon: 'exchanges',
    label: '포인트전환',
    route: { name: 'exchanges' }
  },
  // {
  //   icon: 'threads',
  //   label: '게시판',
  //   route: { name: 'threads' }
  // },
  // {
  //   icon: 'lottery-wheel',
  //   label: '스핀룰렛',
  //   route: { name: 'lottery.wheel' }
  // }
]

onClickOutside(tooltip, () => {
  show.value = false
})

</script>
<style scoped>
.item {
  @apply flex items-center gap-2 rounded overflow-hidden dark:bg-dark-1 light:bg-light-1 h-10 p-2
}
</style>